// components/CoachPerformanceDashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Line, ReferenceLine, Area, Cell, ComposedChart, PieChart, Pie, Legend } from "recharts";
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  addMonths,
  addYears,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  isValid as isValidDate,
} from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight, TrendingUp, Calendar, Users, CheckCircle2, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserStore } from "@/store/userStore";
import { getUserProfilesBulk } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { CoachKpiCard } from "@/components/coach/dashboard/CoachKpiCard";

/** ---------- Types ---------- */
type Period = "week" | "month" | "year";

type Booking = {
  id: string;
  class_id: string | null;
  user_id: string | null;
  class_date: string; // yyyy-mm-dd
  status: string | null;
  time: string;
};

type ClassRow = {
  id: string;
  name: string | null;
  category_id: string | null;
};

type Assignment = {
  id: string;
  class_booking_id: string;
  coach_id: string;
  created_at: string | null;
};

type EnrichedAssignment = Assignment & {
  booking?: Booking;
  klass?: ClassRow;
  userName?: string;
};

/** ---------- Palette couleurs pour catégories ---------- */
const PALETTE = [
  "hsl(var(--primary))",
  "hsl(var(--primary) / 0.76)",
  "hsl(var(--schedule-limited))",
  "hsl(var(--muted-foreground))",
  "hsl(var(--muted))",
];

/** ---------- Hook data ---------- */
function useCoachPerformanceData(coachId: string | undefined, startDate: string, endDate: string) {
  const { t } = useTranslation("coaches");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<EnrichedAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!coachId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      // 1) Attributions du coach — paginé pour passer la limite Supabase de 1000
      let assigns: Assignment[] = [];
      try {
        const PAGE = 1000;
        let from = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, error: aErr } = await supabase
            .from("class_booking_coach")
            .select("id, class_booking_id, coach_id, created_at")
            .eq("coach_id", coachId)
            .range(from, from + PAGE - 1);
          if (aErr) throw aErr;
          const chunk = (data ?? []) as Assignment[];
          assigns = assigns.concat(chunk);
          if (chunk.length < PAGE) break;
          from += PAGE;
        }
      } catch (e: any) {
        console.error("[CoachPerformance] class_booking_coach failed:", e);
        if (!alive) return;
        setError(e?.message ?? t("performance.assignmentsLoadFailed", "Échec du chargement des affectations"));
        setRows([]);
        setLoading(false);
        return;
      }

      // 2) Bookings reliés (batch par 500 IDs pour éviter URL trop longue)
      const uniqueBookingIds = Array.from(new Set(assigns.map(a => a.class_booking_id)));
      let bookings: Booking[] = [];
      try {
        const BATCH = 500;
        for (let i = 0; i < uniqueBookingIds.length; i += BATCH) {
          const slice = uniqueBookingIds.slice(i, i + BATCH);
          const { data, error: bErr } = await supabase
            .from("class_bookings")
            .select("id, class_id, time, user_id, class_date, status")
            .in("id", slice);
          if (bErr) throw bErr;
          bookings = bookings.concat((data ?? []) as Booking[]);
        }
      } catch (e: any) {
        console.error("[CoachPerformance] class_bookings failed:", e);
        if (!alive) return;
        setError(e?.message ?? t("performance.bookingsLoadFailed", "Échec du chargement des réservations"));
        setRows([]);
        setLoading(false);
        return;
      }

      // 3) Filtre période sur class_date
      const filtered = assigns.filter((a) => {
        const booking = bookings.find((b) => b.id === a.class_booking_id);
        if (!booking?.class_date) return false;
        return booking.class_date >= startDate && booking.class_date <= endDate;
      });

      // 4) Cours — non bloquant (les KPIs n'en dépendent pas)
      const classIds = Array.from(
        new Set(
          filtered
            .map((a) => bookings.find((b) => b.id === a.class_booking_id)?.class_id)
            .filter(Boolean)
        )
      ) as string[];

      let classes: ClassRow[] = [];
      if (classIds.length) {
        try {
          const { data, error: cErr } = await supabase
            .from("scheduled_classes")
            .select("id, name, category_id")
            .in("id", classIds);
          if (cErr) throw cErr;
          classes = (data ?? []) as ClassRow[];
        } catch (e: any) {
          console.warn("[CoachPerformance] scheduled_classes failed (non-blocking):", e);
        }
      }

      // 5) Profils adhérents — non bloquant (seuls les noms en pâtissent)
      const userIds = Array.from(
        new Set(
          filtered
            .map((a) => bookings.find((b) => b.id === a.class_booking_id)?.user_id)
            .filter(Boolean)
        )
      ) as string[];

      let profiles: { id: string; first_name: string | null; last_name: string | null }[] = [];
      if (userIds.length) {
        try {
          const { data: profs, error: pErr } = await getUserProfilesBulk(userIds);
          if (pErr) throw pErr;
          profiles = profs ?? [];
        } catch (e: any) {
          console.warn("[CoachPerformance] profiles failed (non-blocking):", e);
        }
      }

      const joined: EnrichedAssignment[] = filtered.map((a) => {
        const booking = bookings.find((b) => b.id === a.class_booking_id);
        const klass = classes.find((c) => c.id === (booking?.class_id ?? ""));
        const profile = profiles.find((p) => p.id === (booking?.user_id ?? ""));
        const userName = profile ? `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() : undefined;
        return { ...a, booking, klass, userName };
      });

      if (!alive) return;
      setRows(joined);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [coachId, startDate, endDate, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);
  return { loading, rows, error, reload };
}

/** ---------- Composant principal ---------- */
export default function CoachPerformanceDashboard() {
  const { t, i18n } = useTranslation(["coach", "coaches", "common"]);
  const locale = i18n.language === "fr" ? fr : enUS;
  const isMobile = useIsMobile();
  const { user, role, isLoadingGymSwitch } = useUserStore();
  const coachId = user?.id;
  const isCoach = role === "coach";

  // Filtres
  const [period, setPeriod] = useState<Period>("month");
  const [offset, setOffset] = useState(0);

  // Période courante
  const [startD, endD] = useMemo(() => {
    const today = new Date();
    if (period === "week") {
      const base = addWeeks(today, -offset);
      return [startOfWeek(base, { weekStartsOn: 1 }), endOfWeek(base, { weekStartsOn: 1 })] as const;
    }
    if (period === "year") {
      const base = addYears(today, -offset);
      return [startOfYear(base), endOfYear(base)] as const;
    }
    const base = addMonths(today, -offset);
    return [startOfMonth(base), endOfMonth(base)] as const;
  }, [period, offset]);

  const startDate = format(startD, "yyyy-MM-dd");
  const endDate = format(endD, "yyyy-MM-dd");

  // Datasource — n'interroge la base que si on a confirmé le rôle coach
  const { loading, rows, error, reload } = useCoachPerformanceData(
    isCoach ? coachId : undefined,
    startDate,
    endDate
  );

  /** ---------- KPI ---------- */
  const kpis = useMemo(() => {
    const total = rows.length;
    const completed = rows.filter((r) => r.booking?.status === "completed").length;
    const upcoming = rows.filter((r) => r.booking?.status === "upcoming").length;
    const uniqueMembers = new Set(rows.map((r) => r.booking?.user_id).filter(Boolean)).size;

    const daysCount =
      Math.max(
        1,
        eachDayOfInterval({ start: startD, end: endD }).length
      );
    const perDay = (total / daysCount).toFixed(1);
    const completionRate = total ? Math.round((completed / total) * 100) : 0;

    return { total, completed, upcoming, uniqueMembers, perDay, completionRate };
  }, [rows, startD, endD]);

  /** ---------- Streak & objectif (motivation) ---------- */
  const { lastActiveDate } = useMemo(() => {
    // Jours dupliqués => set
    const days = Array.from(
      new Set(
        rows
          .filter((r) => r.booking?.status === "completed")
          .map((r) => r.booking!.class_date)
      )
    ).sort();
    if (days.length === 0) return { lastActiveDate: null as string | null };

    return { lastActiveDate: days[days.length - 1] };
  }, [rows]);

  // Objectif mensuel simple (modifiable si besoin)
  const monthlyTarget = 150;
  const progress = Math.min(100, Math.round((kpis.completed / monthlyTarget) * 100));

  /** ---------- Données graphe “évolution” ---------- */
  const trendData = useMemo(() => {
    // Bucket par jour avec zéro-fill pour lisibilité & lignes continues
    const allDays = eachDayOfInterval({ start: startD, end: endD }).map((d) => format(d, "yyyy-MM-dd"));
    const counts = new Map<string, number>();
    rows.forEach((r) => {
      const key = r.booking?.class_date;
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return allDays.map((d) => ({ date: d, value: counts.get(d) ?? 0 }));
  }, [rows, startD, endD]);

  const averageLine = useMemo(() => {
    const sum = trendData.reduce((s, x) => s + x.value, 0);
    return trendData.length ? sum / trendData.length : 0;
  }, [trendData]);


  /** ---------- Répartition par cours ---------- */
  const byCourse = useMemo(() => {
    const bucket = new Map<string, number>();
    rows.forEach((r) => {
      const label = r.klass?.name || t("coach:labels.course", "Cours");
      bucket.set(label, (bucket.get(label) ?? 0) + 1);
    });
    const sorted = Array.from(bucket.entries()).sort((a, b) => b[1] - a[1]);
    const MAX = 8;
    const top = sorted.slice(0, MAX);
    const rest = sorted.slice(MAX);
    const othersSum = rest.reduce((s, [, v]) => s + v, 0);
    const final = othersSum ? [...top, [t("coach:labels.others", "Autres"), othersSum] as const] : top;

    return final.map(([name, value], i) => ({
      name,
      value,
      color: PALETTE[i % PALETTE.length],
    }));
  }, [rows, t]);

  /** ---------- UI Helpers ---------- */
  const periodLabel = useMemo(() => {
    if (period === "week")
      return `${format(startD, "dd MMM", { locale })} – ${format(endD, "dd MMM yyyy", { locale })}`;
    if (period === "year") return format(startD, "yyyy", { locale });
    return format(startD, "MMMM yyyy", { locale });
  }, [period, startD, endD, locale]);

  const formatTickX = (tick: string) => {
    const d = parseISO(tick);
    return format(d, isMobile ? "d" : "dd/MM", { locale });
  };

  const onPrev = () => setOffset((v) => v + 1);
  const onNext = () => setOffset((v) => Math.max(0, v - 1));
  const canGoNext = offset > 0;

  /** ---------- Tooltip personnalisé ---------- */
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const d = parseISO(label);
    return (
      <div className="rounded-xl border bg-background/95 backdrop-blur px-3 py-2 shadow-sm">
        <div className="text-[11px] text-muted-foreground">{format(d, "EEEE dd MMM yyyy", { locale })}</div>
        <div className="text-sm font-medium">{t("coach:charts.sessions", "Séances")}: {payload[0].value}</div>
      </div>
    );
  };

  // Garde-fou rôle : attendre le chargement du rôle/utilisateur puis bloquer les non-coachs
  if (isLoadingGymSwitch || role === null || !user?.id) {
    return (
      <div className="athlete-page-bg min-h-screen p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="h-32 rounded-2xl bg-muted/40 animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[0,1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-muted/40 animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!isCoach) {
    return (
      <div className="athlete-page-bg min-h-screen p-4 md:p-6">
        <div className="max-w-7xl mx-auto rounded-2xl border p-6 text-center text-sm text-muted-foreground">
          {t("coach:access.coach_only", "Accès réservé aux coachs.")}
        </div>
      </div>
    );
  }

  return (
    <div className="coach-page-bg min-h-screen flex flex-col w-full">
      <main className="mx-auto w-full max-w-[54rem] flex-1 px-3 py-4 md:p-6">
        <div className="space-y-4 animate-fade-in pb-20 lg:pb-4">
      {/* Bandeau d'erreur non bloquant */}
      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 flex items-center justify-between gap-3">
          <div className="text-sm text-destructive">
            {t("coach:errors.load_failed", "Impossible de charger toutes les statistiques.")} {error}
          </div>
          <Button size="sm" variant="outline" onClick={reload}>
            {t("common:retry", "Réessayer")}
          </Button>
        </div>
      )}
      {/* Période et navigation */}
      <section className="coach-period-card overflow-hidden p-4 sm:p-5">
        <div className="flex flex-col gap-3">
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground">{t("coach:banner.period", "Période sélectionnée")}</div>
            <div className="truncate text-xl font-bold capitalize">{periodLabel}</div>
          </div>

          <div className="flex items-center gap-2">
              <Select value={period} onValueChange={(v: Period) => { setPeriod(v); setOffset(0); }}>
                <SelectTrigger className="h-11 min-w-0 flex-1 sm:max-w-48">
                  <SelectValue placeholder={t("coach:filters.period", "Période")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{t("coach:filters.week", "Semaine")}</SelectItem>
                  <SelectItem value="month">{t("coach:filters.month", "Mois")}</SelectItem>
                  <SelectItem value="year">{t("coach:filters.year", "Année")}</SelectItem>
                </SelectContent>
              </Select>

              <Button size="icon" variant="outline" className="h-11 w-14 shrink-0" onClick={onPrev} title={t("coach:filters.prev", "Précédent")}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button size="icon" variant="outline" className="h-11 w-14 shrink-0" onClick={onNext} disabled={!canGoNext} title={t("coach:filters.next", "Suivant")}>
                <ChevronRight className="h-5 w-5" />
              </Button>
          </div>
        </div>

        <div className="mt-3 flex items-start gap-2 border-t-2 border-primary pt-3 text-xs text-muted-foreground">
          <Lightbulb className="h-4 w-4 shrink-0 text-primary" />
          <span>
          {progress >= 100
            ? t("coach:banner.motivation_done", "🔥 Objectif atteint — superbe travail !")
            : progress >= 60
              ? t("coach:banner.motivation_keep", "🚀 Belle dynamique, continue sur cette lancée !")
              : t("coach:banner.motivation_push", "💡 Fixe-toi quelques créneaux en plus pour accélérer.")}
          {lastActiveDate ? ` • ${t("coach:banner.last_active", "Dernière séance le")} ${format(parseISO(lastActiveDate), "dd MMM", { locale })}` : ""}
          </span>
        </div>
      </section>

      {/* KPIs (glass + semantic tokens) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CoachKpiCard
          icon={TrendingUp}
          label={t("coach:kpis.assigned", "Séances assignées")}
          value={kpis.total}
          hint={`${kpis.perDay} ${t("coach:kpis.per_day", "/ jour")}`}
          index={0}
          compact
        />
        <CoachKpiCard
          icon={CheckCircle2}
          label={t("coach:kpis.completed", "Séances terminées")}
          value={kpis.completed}
          hint={`${kpis.completionRate}% ${t("coach:kpis.completed_rate", "complété")}`}
          index={1}
          compact
        />
        <CoachKpiCard
          icon={Calendar}
          label={t("coach:kpis.upcoming", "Prochaines séances")}
          value={kpis.upcoming}
          hint={t("coach:kpis.ready", "Prêt à performer 💪")}
          index={2}
          compact
        />
        <CoachKpiCard
          icon={Users}
          label={t("coach:kpis.members", "Athlètes coachés")}
          value={kpis.uniqueMembers}
          hint={t("coach:kpis.current_month", "Mois en cours")}
          index={3}
          compact
        />
      </div>

      {/* Graphes */}
      <div className="grid grid-cols-1 gap-3">
        {/* Évolution */}
        <Card className="coach-chart-card">
        <CardHeader className="px-4 py-3">
            <CardTitle className="text-sm">{t("coach:charts.evolution", "Évolution des coachings")}</CardTitle>
        </CardHeader>
        <CardContent className="px-2 py-2 sm:p-4">
            <div className="h-44 sm:h-64">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                data={trendData}
                margin={isMobile ? { top: 6, right: 8, left: 16, bottom: 0 } : { top: 10, right: 12, left: 24, bottom: 0 }}
                >
                <defs>
                    <linearGradient id="lineFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.05}/>
                    </linearGradient>
                </defs>

                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                    dataKey="date"
                    tickFormatter={formatTickX}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis
                    allowDecimals={false}
                    width={isMobile ? 40 : 48}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: isMobile ? 10 : 12 }}
                    domain={[0, (dataMax: number) => Math.max(1, Math.ceil(dataMax + 1))]} // évite une ligne collée à 0
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={averageLine} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" ifOverflow="extendDomain" />

                {/* Zone de fond */}
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke="transparent"
                    fill="url(#lineFill)"
                    activeDot={false}
                    isAnimationActive={false}
                />
                {/* Ligne par-dessus */}
                <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3 }}
                />
                </ComposedChart>
            </ResponsiveContainer>
            </div>
        </CardContent>
        </Card>


        {/* Répartition par type de cours */}
        <Card className="coach-chart-card">
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-sm">{t("coach:charts.by_course", "Répartition par cours")}</CardTitle>
          </CardHeader>
          <CardContent className="px-2 py-2 sm:p-4">
            <div className="relative h-52 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byCourse} dataKey="value" nameKey="name" innerRadius={isMobile ? 42 : 58} outerRadius={isMobile ? 68 : 88} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                    {byCourse.map((entry, idx) => <Cell key={`cell-${idx}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, color: "hsl(var(--popover-foreground))" }} formatter={(value: number) => [`${value} ${t("coach:charts.sessions", "séances")}`, t("coach:charts.total", "Total")]} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute left-[28%] top-1/2 -translate-x-1/2 -translate-y-1/2 text-center sm:left-[36%]">
                <strong className="block text-2xl">{kpis.total}</strong>
                <span className="text-[11px] text-muted-foreground">{t("coach:charts.sessions", "séances")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dernières séances */}
      <Card className="coach-chart-card">
        <CardHeader className="px-4 py-3">
          <CardTitle className="text-sm">{t("coach:lists.recent_sessions", "Dernières séances")}</CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-4 pb-4">
          <div className="divide-y rounded-xl border overflow-hidden">
            {rows
              .slice()
              .sort((a, b) => (String(b.booking?.class_date) > String(a.booking?.class_date) ? 1 : -1))
              .slice(0, 10)
              .map((r) => (
                <div key={r.id} className="grid grid-cols-5 gap-2 items-center p-3 text-sm">
                  <div className="col-span-2">
                    <div className="font-medium">{r.klass?.name ?? t("coach:labels.course", "Cours")}</div>
                    <div className="text-xs text-muted-foreground">{r.userName ?? "—"}</div>
                  </div>
                  <div className="col-span-1 text-muted-foreground">
                    {r.booking?.class_date
                      ? `${format(parseISO(r.booking.class_date), "dd MMM", { locale })} ${r.booking.time}`
                      : "—"}
                  </div>
                  <div className="col-span-1">
                    <Badge variant="secondary">{t(r.booking?.status, { ns: "common"}) ?? "—"}</Badge>
                  </div>
                  <div className="col-span-1 text-right">
                    <Users className="h-4 w-4 inline-block text-muted-foreground" />
                  </div>
                </div>
              ))}
            {rows.length === 0 && (
              <div className="flex items-center justify-center gap-3 p-5 text-sm text-muted-foreground">
                <Calendar className="h-5 w-5" />
                <span>{t("coach:empty.no_session", "Aucune séance sur la période.")}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* État */}
      {loading && (
        <div className="text-center text-sm text-muted-foreground">
          {t("coach:state.loading", "Chargement des statistiques…")}
        </div>
      )}
      {error && (
        <div className="text-center text-sm text-red-500">
          {t("coach:state.error", "Erreur")} : {error}
        </div>
      )}
        </div>
      </main>
    </div>
  );
}
